import { Controller } from "@hotwired/stimulus";

// Makes Fomantic's `.ui.simple.dropdown` (the ⋮ overflow menus) open on tap.
//
// Read _touch_dropdowns.scss first — it carries the why. In short: a
// `.simple.dropdown` is a CSS-only menu that opens on `:hover`, phones do not
// hover, and the sticky-hover quirk we were relying on is unproven and
// load-bearing (Archive an influencer exists in exactly one menu of this kind).
// Fomantic's own CSS already opens the menu on an `.active` class, which
// nothing in this app sets on a simple dropdown. This controller sets it.
//
// Why one controller on <body>, delegating, rather than 29 mounted instances
// -------------------------------------------------------------------------
// "Tap outside closes" needs a document-level listener no matter how the
// controller is mounted, so a per-element mount buys nothing and costs 29 view
// edits — and, worse, the 30th dropdown someone adds regresses silently. One
// mount point is what makes this a fix for the CLASS of surface instead of
// another per-screen patch. It also covers the Vue-rendered dropdowns and
// anything Turbo streams in, which a HAML-only sweep would miss.
export default class extends Controller {
  connect() {
    // Idempotence, not tidiness. Turbo Drive replaces <body>, so this controller
    // disconnects and reconnects on every navigation, and Stimulus does not
    // promise the old body's disconnect runs before the new body's connect.
    // Listeners here are bound to the DOCUMENT, which outlives every body swap,
    // so install exactly once per document and never tear down: a double-install
    // would toggle `.active` twice per tap (open, then immediately close) and a
    // teardown racing an install would leave the page with no handler at all.
    if (document.documentElement.hasAttribute("data-tap-menus")) return;

    this.coarse = window.matchMedia("(hover: none), (pointer: coarse)");
    this.lastPointerType = null;

    this.onPointerDown = (event) => {
      this.lastPointerType = event.pointerType || null;
    };
    this.onClick = (event) => this.handleClick(event);
    this.onKeydown = (event) => {
      if (event.key === "Escape") this.closeAll();
    };

    // Capture phase: we must see the pointer type and the tap even if something
    // downstream stops propagation.
    document.addEventListener("pointerdown", this.onPointerDown, true);
    document.addEventListener("click", this.onClick, true);
    document.addEventListener("keydown", this.onKeydown);

    // The CSS half is gated on this attribute, so it is only ever set once the
    // listeners above are actually live. Progressive enhancement: no pack, no
    // attribute, no behaviour change.
    document.documentElement.setAttribute("data-tap-menus", "");
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return;
    if (!this.isTouch()) return;

    const dropdown = event.target.closest(".ui.simple.dropdown");

    if (!dropdown) {
      this.closeAll();
      return;
    }

    // A tap inside the dropdown's own menu is the user picking an action — the
    // link handles it. Do NOT close here: hiding the menu between `click` and
    // the navigation it triggers can retarget the tap. Tested by containment
    // rather than `closest(".menu")` because a dropdown can itself sit inside a
    // `.ui.menu` nav bar, which `closest` would match instead.
    const ownMenu = dropdown.querySelector(":scope > .menu");
    if (ownMenu && ownMenu.contains(event.target)) return;

    if (this.fomanticOwns(dropdown)) return;

    const wasActive = dropdown.classList.contains("active");
    this.closeAll();
    if (!wasActive) dropdown.classList.add("active");
  }

  closeAll() {
    document
      .querySelectorAll(".ui.simple.dropdown.active")
      .forEach((element) => {
        if (!this.fomanticOwns(element)) element.classList.remove("active");
      });
  }

  // Fomantic's dropdown module manages `.active` itself wherever it is bound.
  // It is bound to non-simple dropdowns almost everywhere (#sideNav,
  // dropdown.vue, multipleUserSelect.vue), but views/events/table.vue:417 does
  // `$(this.$el).find(".ui.dropdown").dropdown()` over a subtree that
  // contains a `.ui.icon.simple.dropdown.inline` (line 63) — so at least one
  // simple dropdown IS module-backed and already toggles itself on click.
  // Touching it would double-toggle it into a no-op. Fomantic stamps its
  // instance at `module-<namespace>`, namespace defaults to "dropdown"
  // (dropdown.js:67, :4140).
  fomanticOwns(element) {
    const jq = window.jQuery;
    return Boolean(jq && jq.data(element, "module-dropdown"));
  }

  // Is this interaction a touch?
  //
  // Gated on input CAPABILITY, never on viewport width — a touch laptop is a
  // real client and width says nothing about it. Per-interaction where the
  // browser tells us (pointerType), falling back to the device's primary input
  // where it does not.
  isTouch() {
    // A mouse click must NEVER add `.active`. On a hover client the menu is
    // already open from `:hover`; `.active` would then survive the pointer
    // leaving, and the menu would sit there stuck open with nothing to dismiss
    // it. This branch is why a hybrid touch laptop is safe to support: finger
    // taps get `.active`, mouse clicks are refused, and the two never mix.
    if (this.lastPointerType === "mouse") return false;
    if (this.lastPointerType === "touch" || this.lastPointerType === "pen") {
      return true;
    }
    // No pointer information at all — a VoiceOver double-tap activation or a
    // keyboard-generated click. Trust the device's primary input.
    return this.coarse.matches;
  }
}
